"use strict";

/**
 * Events der Geometrie-Bearbeitungsfunktionen.
 *
 * @enum {string}
 */
gc.gis.map.GCMapTransformInteractionEvent = {
	ENTER:          'feature:enter',
	LEAVE:          'feature:leave',
	SELECT:         'feature:select',
	UNSELECT:       'feature:unselect',
	REMOVEDRAWAREA: 'feature:removedrawarea',
	HOVER_VERTEX:   'feature:hoververtex',
	LEAVE_VERTEX:   'feature:leavevertex',

	SCALING:        'feature:scaling',
	TRANSLATING:    'feature:translating',
	ROTATING:       'feature:rotating',

	MODIFY_START:   'feature:modify_start',
	MODIFY_END:     'feature:modify_end'
};

/**
 * Events der Geometrie-Bearbeitungsfunktionen.
 *
 * @enum {int}
 */
gc.gis.map.GCMapTransformInteractionButtons = {
	STYLE:      1,
	SAVE:       2,
	DELETE:     4,
	CLEAR:      8,
	CANCEL:     16
};


/**
 * @classdesc
 * Interface für Geometrie-Bearbeitungsfunktionen.
 *
 * @abstract
 * @class
 * @extends gc.gis.GCWrappable
 * @memberof gc.gis.map
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {gc.gis.map.GCMapView} map Karte, der die Interaktion gehört
 * @param {Object} [opt_options] optionale Aufrufoptionen
 * @param {number} [opt_options.toolbarButtons] definiert die Toolbar-Buttons {@see gc.gis.map.GCMapTransformInteractionButtons}
 * @param {boolean} [opt_options.allowClearDrawingArea=false] Flag welches anzeigt, ob die Inhalte des Tracking-Layer
 *      durch die Funktion "Zeichenfläche leeren", verändert werden dürfen
 * @param {gc.gis.action.GCAbstractAction} [opt_options.clearDrawingAreaAction] Spezielle Aktion die ausgeführt wird, wenn die Funktion
 *    "Zeichenfläche leeren", für den Layer ausgelöst wird (Voraussetzung: das Flag
 *    opt_options.allowClearDrawingArea wurde gesetzt
 * @param {gc.gis.style.GCAbstractStyle[]|Function|ol.style.Style} [opt_options.selectStyle] Style für ein selektiertes Objekt (dieser Style wird bei Bedarf direkt dem Feature zugewiesen)
 * @param {gc.gis.style.GCAbstractStyle[]|Function|ol.style.Style} [opt_options.hoverStyle] Style für ein gehovertes Objekt (dieser Style wird bei Bedarf direkt dem Feature zugewiesen)
 * @param {gc.gis.action.GCAbstractAction} [opt_options.saveAction] Aktion für speichern.
 * @param {gc.gis.action.GCAbstractAction} [opt_options.deleteAction] Aktion für löschen.
 * @param {gc.gis.action.GCAbstractAction} [opt_options.removeDrawAreaAction] Aktion für von Zeichenfläche entfernen.
 * @param {gc.gis.action.GCAbstractAction} [opt_options.cancelAction] Aktion für Bearbeitung beenden.
 */
gc.gis.map.GCMapTransformInteraction = function(map, opt_options)
{
    gc.gis.GCWrappable.call(this);

    gc.gis.assert.isInstance(map, gc.gis.map.GCMapView);

	/**
	 * Kartenobjekt.
	 * @type {gc.gis.map.GCMapView}
	 * @protected
	 */
	this.map = map;
	/**
	 * Definiert die Toolbar-Buttons.
	 * @type {number}
	 * @protected
	 */
	this._toolbarButtons = opt_options.toolbarButtons !== undefined ?
		opt_options.toolbarButtons : gc.gis.map.GCMapTransformInteractionButtons.CANCEL;
	/**
	 * Callback für speichern.
	 * @type {gc.gis.action.GCAbstractAction|undefined}
	 * @protected
	 */
	this._saveAction = opt_options.saveAction;
	/**
	 * Callback für löschen.
	 * @type {gc.gis.action.GCAbstractAction|undefined}
	 * @protected
	 */
	this._deleteAction = opt_options.deleteAction;
	/**
	 * Callback für von Zeichenfläche entfernen.
	 * @type {gc.gis.action.GCAbstractAction|undefined}
	 * @protected
	 */
	this._removeDrawAreaAction = opt_options.removeDrawAreaAction;
	/**
	 * Callback für Bearbeitung beenden.
	 * @type {gc.gis.action.GCAbstractAction|undefined}
	 * @protected
	 */
	this._cancelAction = opt_options.cancelAction;
	/**
	 * Vektorlayer für den die Bearbeitung erfolgen soll.
	 * @type {gc.gis.layer.GCTrackingLayer|null}
	 * @protected
	 */
	this._layerFilter = null;

};
gc.gis.map.GCMapTransformInteraction.prototype = Object.create(gc.gis.GCWrappable.prototype);

/**
 * Setzt einen Vektorlayer für den die Bearbeitung erfolgen soll.
 *
 * @public
 * @method
 *
 * @param {gc.gis.layer.GCTrackingLayer} layer Status Kartenaktion aktiv/deaktiv
 */
gc.gis.map.GCMapTransformInteraction.prototype.setLayerFilter = function(layer)
{
	this._layerFilter = layer;
};

/**
 * Setzt die Karteninteraktion aktiv oder deaktiv.
 *
 * @public
 * @abstract
 * @method
 *
 * @param {boolean} state Status Kartenaktion aktiv/deaktiv
 */
gc.gis.map.GCMapTransformInteraction.prototype.setActive = gc.gis.abstractMethod;


/**
 * Zeigt an, ob die Karteninteraktion aktiv ist.
 *
 * @public
 * @abstract
 * @method
 */
gc.gis.map.GCMapTransformInteraction.prototype.isActive = gc.gis.abstractMethod;

/**
 * Zeigt an, ob die Karteninteraktion aktiv ist.
 *
 * @public
 * @abstract
 * @method
 *
 * @param {gc.gis.feature.GCFeature} feature
 * @return {boolean}
 */
gc.gis.map.GCMapTransformInteraction.prototype.isFeatureSelected = gc.gis.abstractMethod;

/**
 * Liefert die Anzahl der selektierten Features der Karteninteraktion.
 *
 * @public
 * @abstract
 * @method
 *
 * @return {int}
 */
gc.gis.map.GCMapTransformInteraction.prototype.numSelectedFeatures = gc.gis.abstractMethod;

/**
 * Liefert die selektierten Features der Karteninteraktion.
 *
 * @public
 * @abstract
 * @method
 *
 * @return {gc.gis.feature.GCFeature[]}
 */
gc.gis.map.GCMapTransformInteraction.prototype.getSelectedFeatures = gc.gis.abstractMethod;

/**
 * Deselektiert ein Feature der Karteninteraktion.
 *
 * @public
 * @abstract
 * @method
 *
 * @param {gc.gis.feature.GCFeature} feature
 * @param {boolean} opt_silent
 * @return {boolean}
 */
gc.gis.map.GCMapTransformInteraction.prototype.unSelectFeature = gc.gis.abstractMethod;

/**
 * Deselektiert alle Features der Karteninteraktion.
 *
 * @public
 * @abstract
 * @method
 *
 * @param {boolean} opt_silent
 */
gc.gis.map.GCMapTransformInteraction.prototype.unSelectAllFeatures = gc.gis.abstractMethod;

/**
 * Zeigt an, ob das Feature geändert wurde.
 *
 * @public
 * @abstract
 * @method
 *
 * @param {gc.gis.feature.GCFeature} feature
 * @return {boolean}
 */
gc.gis.map.GCMapTransformInteraction.prototype.wasChanged = gc.gis.abstractMethod;

/**
 * Liefert die Anzahl der geänderten Features der Karteninteraktion.
 *
 * @public
 * @abstract
 * @method
 *
 * @return {int}
 */
gc.gis.map.GCMapTransformInteraction.prototype.numChangedFeatures = gc.gis.abstractMethod;

/**
 * Liefert die geänderten Features der Karteninteraktion.
 *
 * @public
 * @abstract
 * @method
 *
 * @return {gc.gis.feature.GCFeature[]}
 */
gc.gis.map.GCMapTransformInteraction.prototype.getChangedFeatures = gc.gis.abstractMethod;
